import React, { useState, useEffect } from 'react';
import styles from './SendBudgetModal.module.css';
import Button from '../ui/button';
import Dropdown from '../ui/dropdown';
import Input from '../ui/input';
import { getCompactAnnotationsSummary, getPredefinedBudgetText } from '../../services/apiService';
import { showErrorToast } from '../../utils/notifications';
import SambaFilePickerModal from './SambaFilePickerModal';

/**
 * Modal para enviar un presupuesto con anotación, asunto, idioma e información
 * Props:
 * - open: boolean (si el modal está visible)
 * - budgetId: number (ID del presupuesto)
 * - expedienteId: number (ID del expediente)
 * - onSend: función para enviar el presupuesto
 * - onCancel: función para cancelar el envío
 */
export default function SendBudgetModal({ 
  open, 
  budgetId,
  expedienteId,
  onSend, 
  onCancel 
}) {
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [subject, setSubject] = useState('Envío presupuesto');
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [information, setInformation] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSambaFilePicker, setShowSambaFilePicker] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [bccEmail, setBccEmail] = useState('');

  const languageOptions = [
    { value: 'ESP', label: 'Español' },
    { value: 'CAT', label: 'Catalán' }
  ];

  // Cargar anotaciones cuando se abre el modal
  useEffect(() => {
    const loadAnnotations = async () => {
      if (open && expedienteId) {
        try {
          setLoading(true);
          const annotationsSummary = await getCompactAnnotationsSummary(expedienteId);
          
          // Filtrar solo anotaciones con gmailMessageId (no vacío)
          const formattedAnnotations = annotationsSummary
            .filter(annotation => annotation.gmailMessageId && annotation.gmailMessageId.trim() !== '')
            .map(annotation => ({
              value: annotation.id,
              label: `${annotation.asunto} - ${annotation.contenido.substring(0, 50)}${annotation.contenido.length > 50 ? '...' : ''}`,
              data: annotation
            }));
          
          setAnnotations(formattedAnnotations);
        } catch (error) {
          console.error('Error loading annotations:', error);
          showErrorToast('Error al cargar las anotaciones');
        } finally {
          setLoading(false);
        }
      }
    };

    loadAnnotations();
  }, [open, expedienteId]);

  // Cargar texto predefinido cuando se selecciona idioma
  useEffect(() => {
    const loadPredefinedText = async () => {
      if (selectedLanguage) {
        try {
          const predefinedTexts = await getPredefinedBudgetText();
          const textForLanguage = predefinedTexts.find(text => text.id === selectedLanguage.value);
          
          if (textForLanguage) {
            setInformation(textForLanguage.body);
          }
        } catch (error) {
          console.error('Error loading predefined text:', error);
          // No mostrar error ya que es opcional
        }
      }
    };

    loadPredefinedText();
  }, [selectedLanguage]);

  // Cargar emails to, cc y bcc cuando se selecciona una anotación
  useEffect(() => {
    if (selectedAnnotation && selectedAnnotation.data) {
      setToEmail(selectedAnnotation.data.toAddresses || '');
      setCcEmail(selectedAnnotation.data.ccAddresses || '');
      setBccEmail(selectedAnnotation.data.bccAddresses || '');
    } else {
      setToEmail('');
      setCcEmail('');
      setBccEmail('');
    }
  }, [selectedAnnotation]);

  const handleSend = () => {
    if (onSend) {
      const sendData = {
        annotationId: selectedAnnotation?.value,
        gmailMessageId: selectedAnnotation?.data?.gmailMessageId, // ID del mensaje de Gmail
        subject,
        language: selectedLanguage?.value,
        information,
        attachments,
        to: toEmail || '',
        cc: ccEmail || '',
        bcc: bccEmail || ''
      };
      onSend(sendData);
      handleReset();
    }
  };

  const handleCancel = () => {
    handleReset();
    if (onCancel) {
      onCancel();
    }
  };

  const handleReset = () => {
    setSelectedAnnotation(null);
    setSubject('Envío presupuesto');
    setSelectedLanguage(null);
    setInformation('');
    setAttachments([]);
    setToEmail('');
    setCcEmail('');
    setBccEmail('');
  };

  const handleSambaFilesSelect = (fileObjects) => {
    // fileObjects es un array de objetos con {path, name, size}
    // Extraer solo los paths como strings
    const newPaths = (fileObjects || []).map(file => {
      if (typeof file === 'string') return file;
      if (file && typeof file === 'object' && file.path) return file.path;
      return null;
    }).filter(path => path); // Remover nulls
    
    // Añadir solo los que no existen ya (evitar duplicados)
    setAttachments(prevFiles => {
      const existingPaths = prevFiles.filter(f => typeof f === 'string');
      const filesToAdd = newPaths.filter(path => !existingPaths.includes(path));
      return [...prevFiles, ...filesToAdd];
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div>
              <h3 className={styles.title}>Enviar Presupuesto</h3>
              <p className={styles.subtitle}>Configura los detalles para enviar el presupuesto</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={handleCancel}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Cargando datos...</p>
            </div>
          ) : (
            <div className={styles.formContainer}>
              <div className={styles.row}>
                <Dropdown
                  label="Anotación"
                  options={annotations}
                  selected={selectedAnnotation}
                  onSelect={setSelectedAnnotation}
                  placeholder="Selecciona una anotación"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Input
                  label="Asunto"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Asunto del envío"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Input
                  label="Para (To)"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="Destinatario principal"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Input
                  label="CC"
                  value={ccEmail}
                  onChange={(e) => setCcEmail(e.target.value)}
                  placeholder="Copia (correos separados por coma)"
                  type="email"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Input
                  label="CCO (BCC)"
                  value={bccEmail}
                  onChange={(e) => setBccEmail(e.target.value)}
                  placeholder="Copia oculta (correos separados por coma)"
                  type="email"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <Dropdown
                  label="Idioma"
                  options={languageOptions}
                  selected={selectedLanguage}
                  onSelect={setSelectedLanguage}
                  placeholder="Selecciona el idioma"
                  style={{ flex: 1 }}
                />
              </div>

              <div className={styles.row}>
                <div className={styles.textareaContainer}>
                  <label className={styles.textareaLabel}>Información</label>
                  <textarea
                    className={styles.textarea}
                    value={information}
                    onChange={(e) => setInformation(e.target.value)}
                    placeholder="Información adicional"
                    rows={10}
                    spellCheck="true"
                  />
                </div>
              </div>

              <div className={styles.row}>
                <div className={styles.attachmentContainer}>
                  <label className={styles.attachmentLabel}>Archivos adjuntos</label>
                  <div className={styles.fileButtonsContainer}>
                    <Button
                      onClick={() => setShowSambaFilePicker(true)}
                      variant="primary"
                    >
                      Seleccionar archivos
                    </Button>
                  </div>
                  
                  {attachments.length > 0 && (
                    <div className={styles.attachmentList}>
                      {attachments.map((file, index) => {
                        // file es una ruta SAMBA string: "/srv/samba/shared/Expedientes/2025/255133/prueba/file.jpg"
                        const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;
                        return (
                          <div key={index} className={styles.attachmentItem}>
                            <span className={styles.fileName}>{fileName}</span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className={styles.removeButton}
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={loading || !subject.trim()}
          >
            Enviar Presupuesto
          </Button>
        </div>
      </div>

      {/* Modal de selección de archivos SAMBA */}
      <SambaFilePickerModal
        isOpen={showSambaFilePicker}
        onClose={() => setShowSambaFilePicker(false)}
        onFilesSelect={handleSambaFilesSelect}
        expId={expedienteId}
        expedienteYear={Math.floor(parseInt(expedienteId.toString().replace(/\s+/g, '')) / 10000) + 2000}
        multiple={true}
      />
    </div>
  );
}